<template>
  <div>
    <b-card class="filter-card mb-2">
      <div class="d-flex align-items-center justify-content-between flex-wrap mb-1">
        <div>
          <b-link class="d-inline-flex align-items-center text-muted mb-50"
            :to="{ name: 'participants-bycourse-page', params: { id: courseId } }">
            <feather-icon icon="ArrowLeftIcon" size="14" class="mr-50" />
            <span>{{ $t('Back') }}</span>
          </b-link>
          <h4 class="mb-0">{{ $t('Add Participants') }}</h4>
          <small class="text-muted">{{ $t('Select employees to add to the course') }}<span v-if="courseName"> — {{
            courseName }}</span></small>
        </div>
      </div>

      <div class="d-flex flex-column flex-md-row mb-1 radio-list">
        <label class="radio-option mb-1 mr-md-1" :class="{ active: searchType === 'MODE_SINGLE' }">
          <input type="radio" v-model="searchType" value="MODE_SINGLE" name="search-type">
          <span>{{ $t('Single Employee') }}</span>
        </label>
        <label class="radio-option mb-1 mr-md-1" :class="{ active: searchType === 'MODE_GROUP' }">
          <input type="radio" v-model="searchType" value="MODE_GROUP" name="search-type">
          <span>{{ $t('Employee by Group') }}</span>
        </label>
        <label class="radio-option mb-1 mr-md-1" :class="{ active: searchType === 'MODE_DEPARTMENT' }">
          <input type="radio" v-model="searchType" value="MODE_DEPARTMENT" name="search-type">
          <span>{{ $t('Employee by Department') }}</span>
        </label>
        <label class="radio-option mb-1 mr-md-1" :class="{ active: searchType === 'MODE_TITLE' }">
          <input type="radio" v-model="searchType" value="MODE_TITLE" name="search-type">
          <span>{{ $t('Employee by Title') }}</span>
        </label>
        <label class="radio-option mb-1" :class="{ active: searchType === 'MODE_ALL' }">
          <input type="radio" v-model="searchType" value="MODE_ALL" name="search-type">
          <span>{{ $t('All Employees') }}</span>
        </label>
      </div>

      <div v-if="searchType === 'MODE_SINGLE'" class="picker-wrap">
        <h6 class="text-muted">{{ $t('Select Employee') }}</h6>
        <member-picker @selectionChanged="onSingleMemberChanged" />

        <div v-if="selectedSingleMember" class="selected-single-preview mt-1 d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <b-avatar size="36" :src="selectedSingleMember.photo" :text="avatarText(selectedSingleMember.fullname)" class="mr-1" />
            <div>
              <div class="font-weight-bold">{{ selectedSingleMember.fullname }}</div>
              <small class="text-muted">{{ selectedSingleMember.username || selectedSingleMember.email }}</small>
            </div>
          </div>
          <b-button variant="link" size="sm" class="p-0" @click="clearSelection">{{ $t('Clear all') }}</b-button>
        </div>
      </div>
      <div v-if="searchType === 'MODE_GROUP'" class="picker-wrap">
        <h6 class="text-muted">{{ $t('Select Group') }}</h6>
        <group-picker :initialCategories="groupSubject" @selectionChanged="onGroupChanged" />
      </div>
      <div v-if="searchType === 'MODE_DEPARTMENT'" class="picker-wrap">
        <h6 class="text-muted">{{ $t('Select Department') }}</h6>
        <department-picker :initialCategories="departmentSubject" @selectionChanged="onDepartmentChanged" />
      </div>
      <div v-if="searchType === 'MODE_TITLE'" class="picker-wrap">
        <h6 class="text-muted">{{ $t('Select Title') }}</h6>
        <title-picker :initialCategories="titleSubject" @selectionChanged="onTitleChanged" />
      </div>

      <div v-if="searchType === 'MODE_ALL'" class="mt-1">
        <b-form-checkbox v-model="notEnrolledOnly" switch>
          {{ $t('Show only users not enrolled in course') }}
        </b-form-checkbox>
      </div>
    </b-card>

    <b-card v-if="searchType !== 'MODE_SINGLE'" no-body class="mb-0">
      <div class="m-2">
        <div v-if="isFetchingNotEnrolled" class="mb-1">
          <b-progress :value="loadProgress" max="100" height="8px" class="mb-50" />
          <small class="text-muted">{{ $t('Loading') }}... {{ loadProgress }}%</small>
        </div>
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label>{{ $t('Show') }}</label>
            <v-select v-model="perPage" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :options="perPageOptions"
              :clearable="false" class="per-page-selector d-inline-block mx-50" />
            <label>{{ $t('entries') }}</label>
          </b-col>
          <b-col md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input v-model="searchQuery" class="d-inline-block" :placeholder="$t('Search...')" />
            </div>
          </b-col>
        </b-row>

        <b-row v-if="selectedCount || canSelectAllMatching" class="mt-1">
          <b-col cols="12" class="d-flex align-items-center justify-content-end flex-wrap">
            <small class="text-muted mr-1">
              {{ selectedCount }} {{ $t('selected') }}
            </small>
            <b-button v-if="canSelectAllMatching" variant="link" size="sm" class="p-0 mr-1"
              :disabled="isSelectingAllMatching" @click="selectAllMatching">
              <b-spinner v-if="isSelectingAllMatching" small class="mr-25" />
              {{ $t('Select all') }} ({{ totalUsers }})
            </b-button>
            <b-button v-if="selectedCount" variant="link" size="sm" class="p-0" @click="clearSelection">
              {{ $t('Clear all') }}
            </b-button>
          </b-col>
        </b-row>
      </div>

      <b-table ref="refTable" class="position-relative" :items="tableItems" responsive :fields="tableColumns"
        primary-key="id" show-empty :empty-text="emptyText" :busy="isLoading">
        <template #head(selected)>
          <b-form-checkbox :checked="isAllSelectedOnPage" :disabled="!tableItems.length"
            @change="toggleSelectAllOnPage" />
        </template>

        <template #head()="data">
          <span v-if="data.column !== 'selected'" class="text">{{ $t(data.label).toUpperCase() }}</span>
        </template>

        <template #cell(selected)="data">
          <b-form-checkbox :checked="isRowSelected(data.item)" @change="toggleRow(data.item)" />
        </template>

        <template #cell(fullname)="data">
          <b-media vertical-align="center">
            <template #aside>
              <b-avatar size="32" :text="avatarText(data.item.fullname)" />
            </template>
            <span class="font-weight-bold d-block text-nowrap">{{ data.item.fullname }}</span>
            <small class="text-muted">{{ data.item.username || data.item.email }}</small>
          </b-media>
        </template>

        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle mr-1" />
            <strong>{{ $t('Loading') }}...</strong>
          </div>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">{{ $t('Showing') }} {{ dataMeta.from }} {{ $t('to') }} {{ dataMeta.to }}
              {{ $t('of') }} {{ dataMeta.of }} {{ $t('entries') }}</span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination v-model="currentPage" :total-rows="totalUsers" :per-page="perPage" first-number last-number
              class="mb-0 mt-1 mt-sm-0" prev-class="prev-item" next-class="next-item">
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <div class="add-participants-footer">
      <div class="d-flex align-items-center justify-content-between flex-wrap">
        <small class="text-muted mb-1 mb-md-0">
          {{ selectedCount }} {{ $t('selected') }}
        </small>
        <div>
          <b-button variant="outline-secondary" class="mr-1"
            :to="{ name: 'participants-bycourse-page', params: { id: courseId } }">
            {{ $t('Cancel') }}
          </b-button>
          <b-button variant="primary" :disabled="!selectedCount || submitting" @click="handleAddParticipants">
            <b-spinner v-if="submitting" small class="mr-50" />
            {{ $t('Add') }} <span v-if="selectedCount">({{ selectedCount }})</span>
          </b-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
  BPagination, BFormCheckbox, BSpinner, BProgress,
} from 'bootstrap-vue'
import { ref, computed, watch } from '@vue/composition-api'
import vSelect from 'vue-select'
import store from '@/store'
import router from '@/router'
import axios from '@axios'
import { avatarText } from '@core/utils/filter'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

import MemberPicker from '@/views/members/component/MemberPicker.vue'
import GroupPicker from '@/views/clientgroups/components/GroupSelector.vue'
import DepartmentPicker from '@/views/department/components/DepartmentPicker.vue'
import TitlePicker from '@/views/members/component/MemberTitlePicker.vue'

import clientGroupStoreModule from '../../clientgroups/clientGroupStoreModule'
import memberStoreModule from '../../members/memberStoreModule'
import participantStoreModule from '../participantStoreModule'

import useHybridPagination from './useHybridPagination'
import useMultiSelectTable from './useMultiSelectTable'
import useNotEnrolledMembers from './useNotEnrolledMembers'

const CLIENT_GROUPS_MODULE = 'client-groups'
const MEMBERS_MODULE = 'apps-members'
const PARTICIPANTS_MODULE = 'apps-participants'

export default {
  components: {
    BCard, BRow, BCol, BFormInput, BButton, BTable, BMedia, BAvatar, BLink,
    BPagination, BFormCheckbox, BSpinner, BProgress,
    vSelect,
    MemberPicker,
    GroupPicker,
    DepartmentPicker,
    TitlePicker,
  },
  setup(_, { root }) {
    const courseId = ref(root.$route.params.id)
    const courseName = ref('')
    const searchType = ref('MODE_SINGLE')
    const notEnrolledOnly = ref(false)
    const groupSubject = ref(null)
    const departmentSubject = ref(null)
    const titleSubject = ref(null)
    const groupSelection = ref(null)
    const departmentSelection = ref(null)
    const titleSelection = ref(null)
    const isLoading = ref(false)
    const submitting = ref(false)

    if (!store.hasModule(CLIENT_GROUPS_MODULE)) {
      store.registerModule(CLIENT_GROUPS_MODULE, clientGroupStoreModule)
    }
    if (!store.hasModule(MEMBERS_MODULE)) {
      store.registerModule(MEMBERS_MODULE, memberStoreModule)
    }
    if (!store.hasModule(PARTICIPANTS_MODULE)) {
      store.registerModule(PARTICIPANTS_MODULE, participantStoreModule)
    }

    const isClientPaginatedMode = computed(() => (
      searchType.value === 'MODE_DEPARTMENT'
      || searchType.value === 'MODE_TITLE'
      || (searchType.value === 'MODE_ALL' && notEnrolledOnly.value)
    ))

    const pagination = useHybridPagination({ isClientPaginatedMode })
    const selection = useMultiSelectTable({
      tableItems: pagination.tableItems,
      totalUsers: pagination.totalUsers,
      filteredFullList: pagination.filteredFullList,
      isClientPaginatedMode,
    })

    const fetchCourseSubscribers = () => store
      .dispatch(`${PARTICIPANTS_MODULE}/fetchParticipantsByCourse`, {
        courseGuid: courseId.value,
        page: 1,
        limit: 5000,
      })
      .then(response => (response.data || {}).data || [])

    const notEnrolled = useNotEnrolledMembers({
      courseId,
      fullList: pagination.fullList,
      fetchCourseSubscribers,
    })

    const tableColumns = computed(() => {
      if (searchType.value === 'MODE_ALL') {
        return [
          { key: 'selected', label: '', sortable: false, class: 'text-center', thStyle: { width: '48px' } },
          { key: 'membercode', label: 'Register No', sortable: false },
          { key: 'fullname', label: 'Full Name', sortable: false },
          { key: 'platformdepartmentname', label: 'Department', sortable: false },
        ]
      }
      return [
        { key: 'selected', label: '', sortable: false, class: 'text-center', thStyle: { width: '48px' } },
        { key: 'fullname', label: 'Full Name', sortable: false },
        { key: 'workTitle', label: 'Work Title', sortable: false },
      ]
    })

    const hasActiveSelection = computed(() => {
      if (searchType.value === 'MODE_GROUP') return !!groupSelection.value
      if (searchType.value === 'MODE_DEPARTMENT') return !!departmentSelection.value
      if (searchType.value === 'MODE_TITLE') return !!titleSelection.value
      return true
    })

    const emptyText = computed(() => {
      if (!hasActiveSelection.value) return root.$t('Choose an option above to view employees')
      return root.$t('No matching records found')
    })

    const selectedSingleMember = computed(() => {
      if (searchType.value !== 'MODE_SINGLE' || !selection.selectedCount.value) return null
      return selection.selectedMap.value[selection.selectedIds.value[0]]
    })

    function notifyError() {
      root.$toast({
        component: ToastificationContent,
        position: 'top-right',
        props: {
          title: root.$t('Error'),
          icon: 'AlertOctagonIcon',
          variant: 'danger',
          text: root.$t('Failed to load all users'),
        },
      })
    }

    function fetchGroupMembers() {
      if (!groupSelection.value) return
      isLoading.value = true
      store.dispatch(`${CLIENT_GROUPS_MODULE}/fetchGroupMembers`, {
        id: groupSelection.value.id,
        page: pagination.currentPage.value,
        limit: pagination.perPage.value,
        search: pagination.debouncedSearchQuery.value,
      }).then(response => {
        const { users, total } = response.data
        pagination.serverItems.value = users || []
        pagination.serverTotal.value = typeof total === 'number' ? total : (users ? users.length : 0)
      }).catch(notifyError).finally(() => {
        isLoading.value = false
      })
    }

    function fetchAllEmployees() {
      isLoading.value = true
      store.dispatch(`${MEMBERS_MODULE}/fetchAllMembers`, {
        currentPage: pagination.currentPage.value,
        perPage: pagination.perPage.value,
        searchQuery: pagination.debouncedSearchQuery.value,
      }).then(response => {
        const { users, total } = response.data
        pagination.serverItems.value = users || []
        pagination.serverTotal.value = typeof total === 'number' ? total : (users ? users.length : 0)
      }).catch(notifyError).finally(() => {
        isLoading.value = false
      })
    }

    function fetchDepartmentMembers() {
      if (!departmentSelection.value) return
      isLoading.value = true
      const deptId = departmentSelection.value.departmentplatformid
        && departmentSelection.value.departmentplatformid.length > 0
        ? departmentSelection.value.departmentplatformid
        : departmentSelection.value.id
      axios.get(`/admin/f/cm/getmembersbydepartment?id=${deptId}`)
        .then(response => {
          pagination.fullList.value = response.data.data || []
        })
        .catch(notifyError)
        .finally(() => {
          isLoading.value = false
        })
    }

    function fetchTitleMembers() {
      if (!titleSelection.value) return
      isLoading.value = true
      const titleName = titleSelection.value.title || titleSelection.value
      axios.get(`/admin/f/clientmembersbytitle/${encodeURIComponent(titleName)}`)
        .then(response => {
          pagination.fullList.value = response.data.data || []
        })
        .catch(notifyError)
        .finally(() => {
          isLoading.value = false
        })
    }

    function fetchServerData() {
      if (searchType.value === 'MODE_ALL') {
        if (notEnrolledOnly.value) return
        fetchAllEmployees()
      } else if (searchType.value === 'MODE_GROUP' && groupSelection.value) {
        fetchGroupMembers()
      }
    }

    async function loadNotEnrolledListWithLoading() {
      isLoading.value = true
      await notEnrolled.loadNotEnrolledList({ onError: notifyError })
      isLoading.value = false
    }

    function onNotEnrolledOnlyChanged() {
      if (searchType.value !== 'MODE_ALL') return
      pagination.currentPage.value = 1
      selection.clearSelection()
      if (notEnrolledOnly.value) {
        pagination.serverItems.value = []
        pagination.serverTotal.value = 0
        loadNotEnrolledListWithLoading()
      } else {
        pagination.fullList.value = []
        notEnrolled.resetNotEnrolledState()
        fetchAllEmployees()
      }
    }

    function onSingleMemberChanged(payload) {
      selection.setSingleSelection(payload)
    }

    function onGroupChanged(payload) {
      groupSelection.value = payload && payload.length ? payload[0] : null
      pagination.currentPage.value = 1
      selection.allMatchingSelected.value = false
      if (groupSelection.value) fetchGroupMembers()
      else {
        pagination.serverItems.value = []
        pagination.serverTotal.value = 0
      }
    }

    function onDepartmentChanged(payload) {
      departmentSelection.value = payload || null
      pagination.currentPage.value = 1
      selection.allMatchingSelected.value = false
      if (departmentSelection.value) fetchDepartmentMembers()
      else pagination.fullList.value = []
    }

    function onTitleChanged(payload) {
      titleSelection.value = payload || null
      pagination.currentPage.value = 1
      selection.allMatchingSelected.value = false
      if (titleSelection.value) fetchTitleMembers()
      else pagination.fullList.value = []
    }

    function selectAllMatching() {
      selection.selectAllMatching({
        onError: notifyError,
        fetchRemote: () => {
          const limit = pagination.totalUsers.value || pagination.perPage.value
          if (searchType.value === 'MODE_ALL') {
            return store.dispatch(`${MEMBERS_MODULE}/fetchAllMembers`, {
              currentPage: 1,
              perPage: limit,
              searchQuery: pagination.debouncedSearchQuery.value,
            }).then(response => response.data.users)
          }
          if (searchType.value === 'MODE_GROUP' && groupSelection.value) {
            return store.dispatch(`${CLIENT_GROUPS_MODULE}/fetchGroupMembers`, {
              id: groupSelection.value.id,
              page: 1,
              limit,
              search: pagination.debouncedSearchQuery.value,
            }).then(response => response.data.users)
          }
          return Promise.resolve([])
        },
      })
    }

    function handleAddParticipants() {
      if (!selection.selectedCount.value || submitting.value) return
      submitting.value = true
      const members = selection.getSelectedMembers()

      store.dispatch(`${PARTICIPANTS_MODULE}/addParticipants`, {
        members,
        courseGuid: courseId.value,
        courseName: courseName.value,
      }).then(() => {
        notEnrolled.markMembersAsEnrolled(members, {
          notEnrolledOnly,
          clearSelection: selection.clearSelection,
        })
        root.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: root.$t('Success'),
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
        router.push({ name: 'participants-bycourse-page', params: { id: courseId.value } })
      }).catch(error => {
        root.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: root.$t('Error'),
            icon: 'AlertOctagonIcon',
            variant: 'warning',
            text: error && error.message,
          },
        })
      }).finally(() => {
        submitting.value = false
      })
    }

    watch(searchType, (newVal, oldVal) => {
      pagination.currentPage.value = 1
      pagination.resetSearch()
      pagination.resetLists()
      selection.allMatchingSelected.value = false

      if (newVal === 'MODE_SINGLE' || oldVal === 'MODE_SINGLE') {
        selection.clearSelection()
      }

      if (searchType.value === 'MODE_ALL') {
        if (notEnrolledOnly.value) loadNotEnrolledListWithLoading()
        else fetchAllEmployees()
      } else if (searchType.value === 'MODE_GROUP' && groupSelection.value) {
        fetchGroupMembers()
      } else if (searchType.value === 'MODE_DEPARTMENT' && departmentSelection.value) {
        fetchDepartmentMembers()
      } else if (searchType.value === 'MODE_TITLE' && titleSelection.value) {
        fetchTitleMembers()
      }
    })

    watch(notEnrolledOnly, onNotEnrolledOnlyChanged)

    watch(() => pagination.currentPage.value, () => {
      if (!isClientPaginatedMode.value) fetchServerData()
    })

    watch(() => pagination.perPage.value, () => {
      pagination.currentPage.value = 1
      if (!isClientPaginatedMode.value) fetchServerData()
    })

    pagination.setupSearchDebounce(() => {
      selection.allMatchingSelected.value = false
      if (!isClientPaginatedMode.value) fetchServerData()
    })

    if (root.$route.query.courseName) {
      courseName.value = root.$route.query.courseName
    } else if (courseId.value) {
      store.dispatch(`${PARTICIPANTS_MODULE}/fetchCourseDetailByGuid`, courseId.value)
        .then(response => {
          const { data } = response.data
          if (data && data.title) courseName.value = data.title
        })
        .catch(() => {})
    }

    return {
      avatarText,
      courseId,
      courseName,
      searchType,
      notEnrolledOnly,
      groupSubject,
      departmentSubject,
      titleSubject,
      isLoading,
      submitting,
      tableColumns,
      isClientPaginatedMode,
      hasActiveSelection,
      emptyText,
      selectedSingleMember,
      perPage: pagination.perPage,
      perPageOptions: pagination.perPageOptions,
      currentPage: pagination.currentPage,
      searchQuery: pagination.searchQuery,
      tableItems: pagination.tableItems,
      totalUsers: pagination.totalUsers,
      dataMeta: pagination.dataMeta,
      selectedCount: selection.selectedCount,
      canSelectAllMatching: selection.canSelectAllMatching,
      isSelectingAllMatching: selection.isSelectingAllMatching,
      isAllSelectedOnPage: selection.isAllSelectedOnPage,
      isRowSelected: selection.isRowSelected,
      toggleRow: selection.toggleRow,
      toggleSelectAllOnPage: selection.toggleSelectAllOnPage,
      clearSelection: selection.clearSelection,
      selectAllMatching,
      isFetchingNotEnrolled: notEnrolled.isFetchingNotEnrolled,
      loadProgress: notEnrolled.loadProgress,
      onSingleMemberChanged,
      onGroupChanged,
      onDepartmentChanged,
      onTitleChanged,
      handleAddParticipants,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.picker-wrap {
  max-width: 480px;
}

.selected-single-preview {
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #f8f7ff;
}

.add-participants-footer {
  position: sticky;
  bottom: 0;
  background: white;
  border-top: 1px solid #e8e8e8;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
  margin-top: 1rem;
}

.radio-option {
  display: inline-flex;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
  margin: 0;

  input {
    display: none;
  }

  &:hover {
    border-color: #7367f0;
    background: #f8f7ff;
  }

  &.active {
    border-color: #7367f0;
    background: #7367f0;
    color: white;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
